import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';
import { withShowHide } from '@sparefoot/react-core/decorators';

import { metaPropTypes, phonePropType } from 'config/propTypes';

import {
	withRouteHandler,
	withSearchHandler,
	withErrorHandler,
} from 'decorators';
import OGFFacilityPage from 'pages/OGFFacilityPage/OGFFacilityPage';
import facilitySelector from 'store/selectors/facility';
import { getFacilityCardPhoneNumber } from 'store/modules/facilities';
import { createBooking } from 'store/modules/bookings';
import { onCreateReview } from 'store/modules/reviews';
import { change as changeFormData } from 'redux-form';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

import { H2 } from 'components/core/Typography';
import { Page, Section, Container } from 'components/core/Page';
import {
	FacilityBreadcrumbs,
	FacilityGallery,
	HowItWorks,
	TopBanner,
	StorageNearYou,
} from 'components/organisms';
import { Expand } from '@sparefoot/react-core';
import FacilityReviews from 'components/organisms/FacilityPage/FacilityReviews/FacilityReviews';
import { ReviewForm } from 'components/review/ReviewForm';
import { ReviewTabs, GoogleReviewCard } from 'components/molecules';
import { SelfStorageSchema } from 'components/facility/SelfStorageSchema';
import { WebPageSchema } from 'components/facility/WebPageSchema';
import { WebSiteSchema } from 'components/facility/WebSiteSchema';
import { UnitProductSchema } from 'components/facility/UnitProductSchema';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { TrackingClient } from '@sparefoot/tracking-client';
import { FacilityDetails } from '../../components/organisms/FacilityPage/FacilityDetails/FacilityDetails';
import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';
import FacilityAmenitiesRanked from 'components/facility/FacilityAmenitiesRanked/FacilityAmenitiesRanked';
import OMIIcon from 'assets/images/icons/omi-Icon.svg';
import Coupon from 'assets/images/icons/coupon.svg';
import { FacilityUnitListV2 } from '../../components/facility/FacilityUnitListV2/FacilityUnitListV2';
import questionMarkIcon from 'assets/images/icons/icon-question-mark.svg';
import './FacilityPageV2.scss';
import { FeaturesAmenitiesAccordion } from 'components/facility/FacilityAmenitiesAccordion/FacilityAmenitiesAccordion';

export const enhance = compose(
	connect(
		facilitySelector,
		{
			changeFormData,
			createBooking,
			onCreateReview,
			getFacilityCardPhoneNumber,
		},
		mapDispatchToActions,
	),
	withErrorHandler,
	withRouteHandler((props) => !get(props, 'facility.location.address')),
	withSearchHandler,
	withShowHide('description'),
	withShowHide('amenities'),
	withPageViewTracking(PAGE_TYPES.FACILITY),
	withProps(() => ({
		scrollToReviews: () => scrollToElement('#facility-page-user-reviews'),
	})),
);

function computeDiscounts(itemsObj, perRow = 3) {
	if (!itemsObj) return { discountItems: [], rows: [] };

	const discountItems = Object.entries(itemsObj)
		.filter(([, value]) => value?.hasAttribute === true)
		.map(([key, value]) => ({ key, label: value.label }));

	const rows = [];
	for (let i = 0; i < discountItems.length; i += perRow) {
		rows.push(discountItems.slice(i, i + perRow));
	}

	return { discountItems, rows };
}

export function FacilityPageV2({
	actions,
	facility,
	phone,
	reviews,
	reviewPageNum,
	reviewSource,
	baseUrl,
	storageUnits,
	vehicleUnits,
	loading,
	trackingPixel,
	meta,
	isNewSearchPage,
	isGoogleReviewEnabled,
	scrollToReviews,
	nearbyFacilities,
	googleReviews,
}) {
	const [reviewFormShowing, setReviewFormShowing] = useState(false);
	useEffect(() => {
		if (!facility?.inNetwork) return;
		TrackingClient.trackFacilityViewed({
			facility_id: facility.id,
			facility_name: facility.name,
			facility_promo: facility.promo,
			facility_rank: facility.rank,
		});
	}, [facility?.id]);

	const { noIndex } = meta;
	const { discountItems: availableDiscounts, rows: discountRows } =
		useMemo(() => {
			const items = facility?.groupedAmenities?.discounts?.items || null;
			return computeDiscounts(items, 3);
		}, [facility?.groupedAmenities?.discounts?.items]);

	const hasGoogleReviews =
		isGoogleReviewEnabled &&
		googleReviews?.reviews &&
		Array.isArray(googleReviews.reviews) &&
		googleReviews.reviews.length > 0;
	const hasVerifiedReviews = Array.isArray(reviews) && reviews.length > 0;

	const renderGoogleReviewCardList = () => (
		<div className="google-review-card-list">
			{googleReviews.reviews.map((review) => (
				<GoogleReviewCard
					key={review.author_name + review.time}
					reviewerName={review.author_name}
					reviewerPhoto={review.profile_photo_url}
					rating={review.rating}
					reviewDate={review.time}
					reviewText={review.text}
				/>
			))}
			{googleReviews.placeId && (
				<div className="google-review-cta">
					<a
						href={`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${googleReviews.placeId}`}
						target="_blank"
						rel="noopener noreferrer"
						className="google-review-cta__button"
					>
						View all reviews on Google
					</a>
				</div>
			)}
		</div>
	);

	if (!facility.inNetwork) {
		return <OGFFacilityPage {...arguments[0]} />;
	}
	return (
		<Page
			className="facility-page-v2"
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.FACILITY}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
			noIndex={noIndex}
			noFollow={noIndex}
		>
			<SelfStorageSchema
				facility={facility}
				phone={phone}
				baseUrl={baseUrl}
				units={{ storageUnits }}
				googleReviews={googleReviews}
			/>
			<WebPageSchema
				facility={facility}
				baseUrl={baseUrl}
			/>
			<WebSiteSchema />
			<UnitProductSchema
				units={[...storageUnits, ...(vehicleUnits || [])]}
				facility={facility}
				baseUrl={baseUrl}
			/>
			<Section className="facility-header-section">
				<Container className="facility-header">
					<FacilityBreadcrumbs
						facility={facility}
						baseUrl={baseUrl}
					/>
				</Container>
				{!facility.isVisible ? (
					<Container className="facility-banner-visible">
						<TopBanner
							className="top-banner__promo"
							content="Online reservations aren't available through SelfStorage. Please contact the facility directly to book."
						/>
					</Container>
				) : (
					<Container>
						<TopBanner
							className="top-banner__promo"
							title="Reserve Now!"
							content="Pricing and availability may change."
						/>
					</Container>
				)}
				<FacilityDetails
					facility={facility}
					reviews={reviews}
					numberOfRatings={facility.reviews?.number}
					phone={phone}
					facilityUrl={facility.url}
					googleReviews={googleReviews}
					isGoogleReviewEnabled={isGoogleReviewEnabled}
					scrollToReviews={scrollToReviews}
					showSchema={false}
				/>
			</Section>
			<FacilityGallery
				facility={facility}
				facilities={nearbyFacilities}
				baseUrl={baseUrl}
			/>
			<Container>
				<Section className="facility-amenities-ranked">
					<Section className="section-heading">
						<H2
							size="large"
							weight="extraBold"
							last
						>
							Key Features
						</H2>
					</Section>
					<FacilityAmenitiesRanked
						groupedAmenities={facility.groupedAmenities}
						storageUnits={storageUnits}
						vehicleUnits={vehicleUnits}
					/>
				</Section>
			</Container>
			{facility.isVisible && (
				<>
					<Section className="facility-units-section">
						<Container>
							<Section>
								{(storageUnits.length > 0 ||
									vehicleUnits?.length > 0) &&
									(availableDiscounts?.length > 0 ||
										facility?.hasOnlineMoveins) && (
										<section className="discount-banners">
											{/* OMI */}
											{facility?.hasOnlineMoveins && (
												<section className="content-container">
													<div className="discount-row discount-row--1">
														<TopBanner
															className="top-banner_omi"
															iconAlt="Online Move In Icon"
															icon={OMIIcon}
															title="Online Move-In Available"
															content="Rent online, skip the counter, and go straight to your space"
														/>
													</div>
												</section>
											)}

											{/* Discounts */}
											{availableDiscounts.length > 0 && (
												<section className="content-container">
													{discountRows.map(
														(row, rowIndex) => (
															<div
																className={`discount-row discount-row--${row.length}`}
																key={rowIndex}
															>
																{row.map(
																	({
																		key,
																		label,
																	}) => (
																		<div
																			className="discount-item"
																			key={
																				key
																			}
																		>
																			<TopBanner
																				className="top-banner_discount"
																				title={label
																					.split(
																						'(',
																					)[0]
																					.trim()}
																				icon={
																					Coupon
																				}
																				iconAlt="Discount Icon"
																				tooltipIcon={
																					questionMarkIcon
																				}
																				tooltipMessage={
																					label
																				}
																			/>
																		</div>
																	),
																)}
															</div>
														),
													)}
												</section>
											)}
										</section>
									)}
							</Section>
							<FacilityUnitListV2
								facility={facility}
								storageUnits={[
									...storageUnits,
									...(vehicleUnits || []),
								]}
								vehicleUnits={vehicleUnits}
								changeFormData={actions.changeFormData}
								onCreateBooking={actions.createBooking}
								isNewSearchPage={isNewSearchPage}
								loading={loading}
							/>
						</Container>
					</Section>
					<Section>
						<Container>
							<FeaturesAmenitiesAccordion
								facility={facility}
								title="Facility Overview & What to Expect"
							/>
						</Container>
					</Section>
					<Section
						id="facility-page-user-reviews"
						className="facility-units-section"
					>
						<Container>
							<div className="reviews-heading-wrapper">
								<H2
									size="large"
									weight="extraBold"
									className="reviews-heading"
								>
									Verified Customer Reviews
								</H2>
								{hasGoogleReviews && !hasVerifiedReviews && (
									<button
										type="button"
										className="leave-review-link"
										onClick={() =>
											setReviewFormShowing(
												(prev) => !prev,
											)
										}
									>
										Leave a review of this facility
									</button>
								)}
							</div>
							{hasGoogleReviews ? (
								hasVerifiedReviews ? (
									<ReviewTabs
										verifiedCount={reviews.length}
										googleCount={
											googleReviews.user_ratings_total
										}
										verifiedContent={
											<FacilityReviews
												reviews={reviews}
												facility={facility}
												reviewPageNum={reviewPageNum}
												baseUrl={baseUrl}
												source={reviewSource}
												onCreateReview={
													actions.onCreateReview
												}
											/>
										}
										googleContent={renderGoogleReviewCardList()}
									/>
								) : (
									<>
										<Expand showing={reviewFormShowing}>
											<div>
												<ReviewForm
													onSubmit={
														actions.onCreateReview
													}
													source={reviewSource}
													facilityId={facility.id}
												/>
											</div>
										</Expand>
										{renderGoogleReviewCardList()}
									</>
								)
							) : (
								<>
									<FacilityReviews
										reviews={reviews}
										facility={facility}
										reviewPageNum={reviewPageNum}
										baseUrl={baseUrl}
										source={reviewSource}
										onCreateReview={actions.onCreateReview}
									/>
								</>
							)}
						</Container>
					</Section>
					<HowItWorks />
				</>
			)}
			<Section className="facility-nearby-facilities">
				<StorageNearYou type="Facility" />
			</Section>
		</Page>
	);
}

FacilityPageV2.propTypes = {
	actions: PropTypes.shape({
		changeFormData: PropTypes.func.isRequired,
		getFacilityCardPhoneNumber: PropTypes.func.isRequired,
		createBooking: PropTypes.func.isRequired,
		onCreateReview: PropTypes.func.isRequired,
	}).isRequired,
	amenities: PropTypes.object.isRequired,
	amenitiesShowing: PropTypes.bool,
	baseUrl: PropTypes.string,
	descriptionShowing: PropTypes.bool.isRequired,
	facility: PropTypes.object.isRequired,
	nearbyFacilities: PropTypes.arrayOf(PropTypes.object),
	loading: PropTypes.bool,
	meta: PropTypes.shape(metaPropTypes),
	onToggleDescription: PropTypes.func.isRequired,
	onToggleAmenities: PropTypes.func.isRequired,
	phone: phonePropType,
	reviews: PropTypes.arrayOf(PropTypes.object),
	reviewPageNum: PropTypes.number,
	reviewSource: PropTypes.string,
	storageUnits: PropTypes.arrayOf(PropTypes.object).isRequired,
	vehicleUnits: PropTypes.arrayOf(PropTypes.object).isRequired,
	trackingPixel: PropTypes.string.isRequired,
	scarcityShow: PropTypes.bool,
	isNewSearchPage: PropTypes.bool,
	isGoogleReviewEnabled: PropTypes.bool,
	scrollToReviews: PropTypes.func,
	googleReviews: PropTypes.object,
};

export default enhance(FacilityPageV2);
